import React, { useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import SendIcon from '@mui/icons-material/Send'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CodeIcon from '@mui/icons-material/Code'
import CloseIcon from '@mui/icons-material/Close'

import { AutoCoderImportBatch } from '../../../types/gotypes'
import {
  useImportRequestAction,
  useJobReceipts,
  useRetryReceipt,
} from '../../../hooks/useJobReceipts'
import useAccount from '../../../hooks/useAccount'
import { useWhoToAsk } from '../../../hooks/useWhoToAsk'
import { decide } from '../../../utils/permissionReason'
import DisabledReason from '../../system/DisabledReason'
import {
  formatImportQuerySummary,
  formatImportTimestamp,
  formatImportWindow,
  importBatchOutcome,
  importBatchOutcomeColor,
  importBatchOutcomeLabel,
  ImportBatchOutcome,
  importDriverLabel,
  IMPORT_TABS,
  isImportRequest,
  type ImportTab,
} from '../../../utils/importBatches'
import RecordsExplorer from '../RecordsExplorer'
import { receiptCanRetry, requestAction } from './receiptActions'

/**
 * The job's Imports — provenance for EVERYTHING this job has fetched,
 * whichever door it came through and whether or not a pipeline ever saw it,
 * and since 22 Oct 2026 the import queue too (design/2026-10-22-import-queue.md):
 * a row that names a driver is a REQUEST for data (a post's comments from the
 * X or YouTube API or Apify, an uploaded file), shown by tab — in progress,
 * scheduled (a refetch booked for later), done, failed — with its driver,
 * target, attempts and next try; a Manager retries a failed request or cancels
 * a waiting one. The All tab is the receipts list as it always was, without
 * the waiting requests.
 *
 * It generalizes the pipeline-scoped Imports tab it replaces: same outcome
 * precedence, same failure/truncation banners, same request-payload viewer,
 * plus the three things a detached ledger needs — a Pipeline column (blank is
 * the normal reading now), a Retry for a fetch that failed or stalled, and a
 * drill-down that works on a receipt with no pipeline at all.
 *
 * Failures and truncations stay PROMINENT: a fetch that quietly imported
 * nothing is the single thing an operator most needs to notice.
 */

interface ReceiptsTableProps {
  customer: string
  job: string
  /** False while the containing tab is not showing — a hidden list must not poll. */
  active?: boolean
  /**
   * Opens the send-to-pipeline flow for a receipt. The row action is rendered
   * only when a handler is supplied, so the ledger never shows a door that
   * leads nowhere.
   */
  onSendToPipeline?: (batch: AutoCoderImportBatch) => void
}

const numberCell = (value: number, emphasise = false) => (
  <TableCell align="right">
    <Typography variant="body2" sx={{ fontWeight: emphasise && value > 0 ? 600 : 400 }}>
      {(value ?? 0).toLocaleString()}
    </Typography>
  </TableCell>
)

export { receiptCanRetry, requestAction }

/** What a request fetches, for its row: the post's link when the request names one. */
const requestTarget = (batch: AutoCoderImportBatch): { label: string; href?: string } => {
  const q = (batch?.query ?? {}) as Record<string, unknown>
  const url = typeof q.url === 'string' && q.url ? q.url : undefined
  const fileName = typeof q.file_name === 'string' ? q.file_name : ''
  return { label: batch.target || fileName || '—', href: url }
}

const ReceiptsTable: React.FC<ReceiptsTableProps> = ({
  customer,
  job,
  active = true,
  onSendToPipeline,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [tab, setTab] = useState<ImportTab>('')
  const [drillDown, setDrillDown] = useState<AutoCoderImportBatch | null>(null)
  const [queryBatch, setQueryBatch] = useState<AutoCoderImportBatch | null>(null)

  const { data, loading, isFetching, error, refetch } = useJobReceipts(
    customer,
    job,
    page,
    rowsPerPage,
    active,
    tab,
  )
  const { retry, retryingId, error: retryError } = useRetryReceipt(customer, job)
  const request = useImportRequestAction(customer, job)
  const account = useAccount()
  const who = useWhoToAsk(account.customer?.name ?? undefined)
  const isManager = account.user?.role_permissions?.isManager ?? false
  const retryDecision = decide(isManager, "You can't retry an import.", who)
  const cancelDecision = decide(isManager, "You can't cancel an import.", who)

  const batches = data?.batches ?? []
  // One instant for the whole page, so two rows requested a second apart cannot
  // land on opposite sides of the stall threshold.
  const now = Date.now()
  const outcomes = new Map(batches.map(b => [b.id, importBatchOutcome(b, now)]))
  const outcomeOf = (batch: AutoCoderImportBatch): ImportBatchOutcome =>
    outcomes.get(batch.id) ?? importBatchOutcome(batch, now)

  const failures = batches.filter(b => outcomeOf(b) === 'failed')
  const truncations = batches.filter(b => outcomeOf(b) === 'truncated')
  const skips = batches.filter(b => outcomeOf(b) === 'skipped')
  const stalled = batches.filter(b => outcomeOf(b) === 'stalled')

  // 13/12 became 11/10: Pipeline, Window and the raw Fetched count all
  // left; the surviving count column IS `mapped`, labelled Fetched.
  const columnCount = onSendToPipeline ? 11 : 10

  return (
    <Box sx={{ p: 3, overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">Imports</Typography>
          <Typography variant="body2" color="text.secondary">
            One row per import into this job — the fetches that failed too, and the requests still
            waiting for their source (each source runs as fast as its own limits allow).
            {data ? ` ${data.total.toLocaleString()} in this view.` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <span>
            <IconButton color="info" onClick={refetch} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_e, next: ImportTab) => {
          setTab(next)
          setPage(0)
        }}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2, minHeight: 36 }}
        data-testid="autocoder-imports-tabs"
      >
        {IMPORT_TABS.map(t => {
          const n = t.count && data?.counts ? data.counts[t.count] : undefined
          return (
            <Tab
              key={t.value || 'all'}
              value={t.value}
              sx={{ minHeight: 36, py: 0.5 }}
              data-testid={`autocoder-imports-tab-${t.value || 'all'}`}
              label={n !== undefined ? `${t.label} (${n.toLocaleString()})` : t.label}
            />
          )
        })}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {request.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {request.error}
        </Alert>
      )}

      {retryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {retryError}
        </Alert>
      )}

      {failures.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>
            {failures.length} failed {failures.length === 1 ? 'fetch' : 'fetches'} on this page
          </AlertTitle>
          {failures.slice(0, 3).map(b => (
            <Typography key={b.id} variant="body2" sx={{ fontFamily: 'monospace' }}>
              {formatImportTimestamp(b.requestedAt)} — {b.error}
            </Typography>
          ))}
          {failures.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              …and {failures.length - 3} more below.
            </Typography>
          )}
        </Alert>
      )}

      {stalled.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {stalled.length} {stalled.length === 1 ? 'fetch has' : 'fetches have'} stopped reporting
          </AlertTitle>
          A fetch that has not written its outcome within an hour is no longer expected to. Retry
          replays the request as a new fetch — the stalled receipt is kept as the record of what
          happened.
        </Alert>
      )}

      {truncations.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {truncations.length} truncated {truncations.length === 1 ? 'fetch' : 'fetches'}
          </AlertTitle>
          A truncated fetch hit its page ceiling before the end of its window, so that window is
          not fully covered. Re-run the affected day before treating it as imported.
        </Alert>
      )}

      {skips.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {skips.length} {skips.length === 1 ? 'fetch' : 'fetches'} with unparseable rows
          </AlertTitle>
          Rows the importer could not parse are a hole in the window's ordered prefix — the cursor
          HOLDS on them, so those rows are not imported. Treat the affected days as incomplete and
          re-run them.
        </Alert>
      )}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )}

      {data && batches.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nothing has been fetched into this job yet. Records ingested before receipts existed
            carry no provenance.
          </Typography>
        </Paper>
      )}

      {batches.length > 0 && (
        <Paper elevation={1}>
          <TableContainer>
            <Table size="small" data-testid="autocoder-receipts-table">
              <TableHead>
                <TableRow>
                  <TableCell>When (UTC)</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Query</TableCell>
                  <TableCell align="right">Pages</TableCell>
                  <TableCell align="right">
                    {/* "Fetched" MEANS THE CODEABLE ROWS (the stored `mapped`
                        count), not the raw upstream total.

                        The raw total was its own column and it made the row
                        look wrong: "fetched 637 ... new 566" with 71
                        unexplained. The 71 are real but they are not ours —
                        parent posts and other upstream message types — so
                        leading with them invited the reader to hunt for a loss
                        that never happened. The raw number is kept in the
                        cell's tooltip rather than deleted, because the gap is
                        still the honest answer to "why 566 and not 637?". */}
                    <Tooltip title="Rows fetched that are records for us — comments and replies. The upstream response also contains the parent posts and other message types; hover a number to see the raw total it came from.">
                      <span>Fetched</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell align="right">Duplicates</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                  <TableCell align="right">Records</TableCell>
                  {onSendToPipeline && <TableCell align="right">Send</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map(batch => {
                  const outcome = outcomeOf(batch)
                  const canRetry = receiptCanRetry(batch, outcome)
                  const action = requestAction(batch, outcome)
                  const target = isImportRequest(batch) ? requestTarget(batch) : null
                  return (
                    <React.Fragment key={batch.id}>
                      <TableRow
                        hover
                        // Keyed by the receipt id, never the page position: a
                        // second fetch lands on top and shifts every index by
                        // one, but a receipt's id is the same row forever.
                        data-testid={`autocoder-receipt-row-${batch.id}`}
                        // A failed fetch gets an error accent and drops its
                        // bottom border, so it reads as one block with the
                        // error row rendered directly beneath it.
                        sx={
                          outcome === 'failed'
                            ? {
                                '& td': { borderBottom: 'none' },
                                borderLeft: 3,
                                borderColor: 'error.main',
                              }
                            : undefined
                        }
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {formatImportTimestamp(batch.requestedAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {batch.requestedBy}
                          </Typography>
                        </TableCell>
                        <TableCell data-testid={`autocoder-receipt-outcome-${batch.id}`}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Chip
                              size="small"
                              label={importBatchOutcomeLabel(outcome)}
                              color={importBatchOutcomeColor(outcome)}
                              variant={outcome === 'empty' ? 'outlined' : 'filled'}
                            />
                            {action && (
                              <DisabledReason
                                reason={(action === 'retry' ? retryDecision : cancelDecision).reason}
                              >
                                <Tooltip title={action === 'retry' ? 'Queue this request again now' : 'Withdraw this request'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      aria-label={action === 'retry' ? 'Retry this import' : 'Cancel this import'}
                                      data-testid={`autocoder-import-${action}-${batch.id}`}
                                      disabled={
                                        request.actingId !== null ||
                                        !(action === 'retry' ? retryDecision : cancelDecision).allowed
                                      }
                                      onClick={() => {
                                        void request.act(batch.id, action).catch(() => {
                                          // Surfaced by the request alert above.
                                        })
                                      }}
                                    >
                                      {request.actingId === batch.id ? (
                                        <CircularProgress size={16} color="info" />
                                      ) : action === 'retry' ? (
                                        <ReplayIcon fontSize="inherit" />
                                      ) : (
                                        <CloseIcon fontSize="inherit" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </DisabledReason>
                            )}
                            {canRetry && (
                              <Tooltip title="Send this request again as a new fetch">
                                {/* data-voice="confirm": Douglas asks before pressing (#1363) — starts a new fetch that spends API budget */}
                                <span data-voice="confirm">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    aria-label="Retry this fetch"
                                    data-testid={`autocoder-receipt-retry-${batch.id}`}
                                    disabled={retryingId !== null}
                                    onClick={() => {
                                      void retry(batch).catch(() => {
                                        // Surfaced by the mutation's error alert.
                                      })
                                    }}
                                  >
                                    {retryingId === batch.id ? (
                                      <CircularProgress size={16} color="info" />
                                    ) : (
                                      <ReplayIcon fontSize="inherit" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                          {isImportRequest(batch) && outcome !== 'failed' && (batch.error || '').trim() !== '' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {batch.error}
                            </Typography>
                          )}
                        </TableCell>
                        {/* SOURCE ALONE. The channel name ("Sky TikTok
                            (Sprinklr)") was a subtitle here and actively
                            misleading: that connector carries Instagram,
                            Facebook, X and YouTube too, so naming one platform
                            in the ledger says something untrue about every row.
                            The window still names the channel where it belongs —
                            in the drill-down's subtitle, next to the dates. */}
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap>
                            {batch.source}
                            {isImportRequest(batch) && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={importDriverLabel(batch.driver)}
                                sx={{ ml: 1 }}
                                data-testid={`autocoder-import-driver-${batch.id}`}
                              />
                            )}
                          </Typography>
                          {target && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {target.href ? (
                                <Link href={target.href} target="_blank" rel="noopener noreferrer">
                                  {target.label}
                                </Link>
                              ) : (
                                target.label
                              )}
                              {batch.attempts > 0 ? ` · attempt ${batch.attempts}` : ''}
                              {batch.nextAttemptAt && (outcome === 'scheduled' || outcome === 'queued')
                                ? ` · next ${formatImportTimestamp(batch.nextAttemptAt)}`
                                : ''}
                              {batch.costUsd > 0 ? ` · $${batch.costUsd.toFixed(2)}` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }} noWrap>
                              {formatImportQuerySummary(batch.query)}
                            </Typography>
                            {batch.query !== undefined && batch.query !== null && (
                              <Tooltip title="View the exact request payload">
                                <IconButton
                                  size="small"
                                  color="info"
                                  aria-label="View the exact request payload"
                                  data-testid={`autocoder-receipt-query-${batch.id}`}
                                  onClick={() => setQueryBatch(batch)}
                                >
                                  <CodeIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        {numberCell(batch.pages)}
                        <TableCell align="right">
                          <Tooltip
                            title={
                              batch.fetched > batch.mapped
                                ? `${batch.fetched.toLocaleString()} rows returned upstream; ${(
                                    batch.fetched - batch.mapped
                                  ).toLocaleString()} were parent posts or other message types, not records for us.`
                                : `${batch.fetched.toLocaleString()} rows returned upstream, all of them records for us.`
                            }
                          >
                            <Typography variant="body2">{(batch.mapped ?? 0).toLocaleString()}</Typography>
                          </Tooltip>
                        </TableCell>
                        {numberCell(batch.new, true)}
                        {numberCell(batch.duplicates)}
                        {numberCell(batch.parseSkipped, true)}
                        <TableCell align="right">
                          <Tooltip title="Show the records this fetch produced">
                            <span>
                              <IconButton
                                size="small"
                                color="info"
                                aria-label="Show the records this fetch produced"
                                data-testid={`autocoder-receipt-records-${batch.id}`}
                                // Only a receipt that plainly produced nothing
                                // is undrillable. A receipt whose finalize
                                // failed keeps its counts at zero while its
                                // records already carry the batch id — exactly
                                // the receipt an operator wants to open — so
                                // anything that saw rows at all stays clickable.
                                disabled={
                                  batch.new === 0 && batch.mapped === 0 && batch.fetched === 0
                                }
                                onClick={() => setDrillDown(batch)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        {onSendToPipeline && (
                          <TableCell align="right">
                            <Tooltip title="Send this fetch's records to a pipeline">
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  aria-label="Send this fetch's records to a pipeline"
                                  data-testid={`autocoder-receipt-send-${batch.id}`}
                                  // Same rule as the drill-down: only a receipt
                                  // that plainly saw nothing has nothing to send.
                                  disabled={
                                    batch.new === 0 && batch.mapped === 0 && batch.fetched === 0
                                  }
                                  onClick={() => onSendToPipeline(batch)}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      {outcome === 'failed' && (
                        <TableRow>
                          <TableCell colSpan={columnCount} sx={{ pt: 0 }}>
                            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {batch.error}
                              </Typography>
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </Paper>
      )}

      <ReceiptRecordsDialog
        customer={customer}
        job={job}
        batch={drillDown}
        onClose={() => setDrillDown(null)}
      />

      <Dialog open={!!queryBatch} onClose={() => setQueryBatch(null)} maxWidth="md" fullWidth>
        <DialogTitle>Request payload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Exactly what was sent upstream, window and paging included.
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '60vh',
              fontSize: '0.75rem',
            }}
          >
            {JSON.stringify(queryBatch?.query ?? null, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setQueryBatch(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

interface ReceiptRecordsDialogProps {
  customer: string
  job: string
  batch: AutoCoderImportBatch | null
  onClose: () => void
}

/**
 * The drill-down: exactly the records one fetch produced, through the shared
 * RecordsExplorer, so a record looks the same wherever it is reached from — and
 * so a DETACHED receipt (no pipeline) can be opened at all, which the old
 * pipeline-scoped drill-down could not do.
 */
const ReceiptRecordsDialog: React.FC<ReceiptRecordsDialogProps> = ({
  customer,
  job,
  batch,
  onClose,
}) => (
  <Dialog
    open={!!batch}
    onClose={onClose}
    maxWidth={false}
    fullWidth
    PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '80vh' } }}
    data-testid="autocoder-receipt-records-dialog"
  >
    <DialogTitle>
      Records from this fetch
      {batch && (
        <Typography variant="body2" color="text.secondary">
          {formatImportTimestamp(batch.requestedAt)} · {batch.source}
          {batch.channel ? ` / ${batch.channel}` : ''} ·{' '}
          {formatImportWindow(batch.windowStart, batch.windowEnd)}
        </Typography>
      )}
    </DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {batch && (
        <RecordsExplorer
          customer={customer}
          job={job}
          door={{ importBatchId: batch.id }}
          emptyMessage="This fetch produced no records."
        />
      )}
    </DialogContent>
    <DialogActions>
      <Button color="info" onClick={onClose}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
)

export default ReceiptsTable
